import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { ThemePreviewIndex, ThemePreview1, ThemePreview2 } from "./pages/theme-preview/ThemePreview";
import { MobileAuthProvider, useMobileAuth } from "./context/MobileAuthContext";
import { MobileThemeProvider } from "./context/MobileThemeContext";
import { LanguageProvider } from "./context/LanguageContext";
import { TierProvider } from "./context/TierContext";
import { ChatProvider } from "./context/ChatContext";
import { MobileChatProvider } from "./context/MobileChatContext";
import { FeatureFlagsProvider } from "./context/FeatureFlagsContext";
import { SuperAdminProvider, useSuperAdmin } from "./context/SuperAdminContext";
import { SupportNotificationsProvider, SuperAdminSupportNotificationsProvider } from "./context/SupportNotificationsContext";
import { Toaster } from "./components/ui/sonner";
import { hasPermission, getUserRoles } from "./lib/permissions";
import Layout from "./components/Layout";
import ChatPanel from "./components/ChatPanel";
import Login from "./pages/Login";
import FreeTrial from "./pages/FreeTrial";
import Features from "./pages/Features";
import SupportPlans from "./pages/SupportPlans";
import OnboardingWizard from "./pages/OnboardingWizard";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import V2ForgotPassword from "./pages/V2ForgotPassword";
import V2ResetPassword from "./pages/V2ResetPassword";
import SetPassword from "./pages/SetPassword";
// Super Admin Pages
import {
  SuperAdminLogin,
  SuperAdminForgotPassword,
  SuperAdminResetPassword,
  SuperAdminForcePasswordChange,
  SuperAdminLayout,
  SuperAdminDashboard,
  TenantsList,
  TenantDetail,
  TenantOnboarding,
  TicketsList,
  TicketDetail,
  AdminsList,
  TwoFactorSetup,
  SystemSettings,
  SessionsManagement,
  RoadmapManagement,
  HelpManagement,
  SuperAdminEmails,
  AuthMigration,
  DemoTenantsManagement,
  FeedbackManagement,
  FeatureFlags,
  Migrations,
  AllocateMigration,
  Announcements
} from "./pages/super-admin";
import Dashboard from "./pages/Dashboard";
import GettingStarted from "./pages/GettingStarted";
import Equipment from "./pages/Equipment";
import Operators from "./pages/Operators";
import Customers from "./pages/Customers";
import Suppliers from "./pages/Suppliers";
import TipsMaterials from "./pages/TipsMaterials";
// Jobs merged into Schedule
import Schedule from "./pages/Schedule";
import TowerCraneJobCreate from "./pages/TowerCraneJobCreate";
import DryHireInvoicingDashboard from "./pages/DryHireInvoicingDashboard";
import MyJobs from "./pages/MyJobs";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import DataManagement from "./pages/DataManagement";
import DataImport from "./pages/DataImport";
import PlanningBoard from "./pages/PlanningBoard";
import Utilisation from "./pages/Utilisation";
import Compliance from "./pages/Compliance";
import Forms from "./pages/Forms";
import Documents from "./pages/Documents";
import Dockets from "./pages/Dockets";
import Timesheets from "./pages/Timesheets";
import ProductRoadmap from "./pages/ProductRoadmap";
import ChatPage from "./pages/ChatPage";
import SignupSuccess from "./pages/SignupSuccess";
import HelpCenter from "./pages/HelpCenter";
import ReleaseNotes from "./pages/ReleaseNotes";
import Support from "./pages/Support";
import TenantTicketDetail from "./pages/TenantTicketDetail";
import Quotes from "./pages/Quotes";
import Projects from "./pages/Projects";
import QuoteEdit from "./pages/QuoteEdit";
import QuoteView from "./pages/QuoteView";
import QuoteApproval from "./pages/QuoteApproval";
import DocketSign from "./pages/DocketSign";
import CompliancePackPublic from "./pages/CompliancePackPublic";
import PolicyPage from "./pages/PolicyPage";
import EarlyAdopterOnboarding from "./pages/EarlyAdopterOnboarding";
// Mobile Pages (Operator)
import MobileLogin from "./pages/mobile/MobileLogin";
import MobileForgotPassword from "./pages/mobile/MobileForgotPassword";
import MobileResetPassword from "./pages/mobile/MobileResetPassword";
import MobileDashboard from "./pages/mobile/MobileDashboard";
import MobileJobs from "./pages/mobile/MobileJobs";
import MobileJobDetail from "./pages/mobile/MobileJobDetail";
import MobileCreateDocket from "./pages/mobile/MobileCreateDocket";
import MobileTimesheet from "./pages/mobile/MobileTimesheet";
import MobileNotifications from "./pages/mobile/MobileNotifications";
import MobileChat from "./pages/mobile/MobileChat";
import MobileForms from "./pages/mobile/MobileForms";
import MobileFormSubmission from "./pages/mobile/MobileFormSubmission";
import MobileSubmissionDetail from "./pages/mobile/MobileSubmissionDetail";
import MobileHelp from "./pages/mobile/MobileHelp";
import MobileCompliance from "./pages/mobile/MobileCompliance";
import MobileQuotes from "./pages/mobile/MobileQuotes";
import MobileQuoteDetail from "./pages/mobile/MobileQuoteDetail";
import MobileQuoteEdit from "./pages/mobile/MobileQuoteEdit";
import MobileSettings from "./pages/mobile/MobileSettings";
import MobileInspections from "./pages/mobile/MobileInspections";
import MobileInspectionDetail from "./pages/mobile/MobileInspectionDetail";
import MobileLeave from "./pages/mobile/MobileLeave";
import MobileImpersonationBanner from "./components/mobile/MobileImpersonationBanner";
import MobileMore from "./pages/mobile/MobileMore";
import MobileDocuments from "./pages/mobile/MobileDocuments";
import MobileManage from "./pages/mobile/MobileManage";
import MobileManageCustomers from "./pages/mobile/MobileManageCustomers";
import MobileManageSites from "./pages/mobile/MobileManageSites";
import MobileManageEquipment from "./pages/mobile/MobileManageEquipment";
import MobileEquipment from "./pages/mobile/MobileEquipment";
import MobileMyDockets from "./pages/mobile/MobileMyDockets";
import MobileMyDocketDetail from "./pages/mobile/MobileMyDocketDetail";
import MobileEquipmentDetail from "./pages/mobile/MobileEquipmentDetail";
import MobileManageSuppliers from "./pages/mobile/MobileManageSuppliers";
import MobileManageWorkerTypes from "./pages/mobile/MobileManageWorkerTypes";
import MobileManageOperators from "./pages/mobile/MobileManageOperators";
import MobileManageDockets from "./pages/mobile/MobileManageDockets";
import MobileDataManagement from "./pages/mobile/MobileDataManagement";
import MobileBusinessSettings from "./pages/mobile/MobileBusinessSettings";
import EnvBanner from "./components/EnvBanner";
import MobileSchedule from "./pages/mobile/MobileSchedule";
import MobileScheduleJobDetail from "./pages/mobile/MobileScheduleJobDetail";
import MobileScheduleCreateJob from "./pages/mobile/MobileScheduleCreateJob";
import ForcePasswordChange from "./components/ForcePasswordChange";
import SessionTimeoutModal from "./components/SessionTimeoutModal";
import SubscriptionRequired from "./pages/SubscriptionRequired";
import MobileSubscriptionRequired from "./pages/mobile/MobileSubscriptionRequired";
import { TourProvider, TourOverlay, SubTourProvider, SubTourOverlay } from "./components/tour";
import { useVersionCheck } from "./hooks/useVersionCheck";
import "./App.css";
import "./styles/mobile-theme.css";

const ProtectedRoute = ({ children, roles, permission, allowSuspended = false }) => {
  const { user, loading, subscriptionStatus } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Suspended or cancelled tenants must subscribe / cannot access the app
  // Unless the route explicitly allows suspended access (e.g. Support)
  if (!allowSuspended && (subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled')) {
    return <SubscriptionRequired />;
  }
  
  // Permission-based check (preferred) — uses the same system as Layout sidebar
  if (permission && !hasPermission(user, permission)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Legacy role-list check — checks against the full roles array, not just primary
  if (roles && !roles.some(r => getUserRoles(user).includes(r))) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Mobile Protected Route
const MobileProtectedRoute = ({ children }) => {
  const { operator, loading, subscriptionStatus } = useMobileAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }
  
  if (!operator) {
    return <Navigate to="/mobile/login" replace />;
  }

  // Suspended tenants — show paywall (admin) or blocked message (worker)
  if (subscriptionStatus === 'suspended') {
    return <MobileSubscriptionRequired />;
  }
  
  return children;
};

// Mobile Routes Component
const MobileRoutes = () => {
  return (
    <MobileThemeProvider>
    <MobileAuthProvider>
      <Routes>
        <Route path="/mobile/login" element={<MobileLogin />} />
        <Route path="/mobile/forgot-password" element={<MobileForgotPassword />} />
        <Route path="/mobile/reset-password" element={<MobileResetPassword />} />
        <Route path="/mobile/dashboard" element={
          <MobileProtectedRoute><MobileDashboard /></MobileProtectedRoute>
        } />
        <Route path="/mobile/jobs" element={
          <MobileProtectedRoute><MobileJobs /></MobileProtectedRoute>
        } />
        <Route path="/mobile/jobs/:jobId" element={
          <MobileProtectedRoute><MobileJobDetail /></MobileProtectedRoute>
        } />
        <Route path="/mobile/docket/create/:jobId" element={
          <MobileProtectedRoute><MobileCreateDocket /></MobileProtectedRoute>
        } />
        <Route path="/mobile/timesheet" element={
          <MobileProtectedRoute><MobileTimesheet /></MobileProtectedRoute>
        } />
        <Route path="/mobile/notifications" element={
          <MobileProtectedRoute><MobileNotifications /></MobileProtectedRoute>
        } />
        <Route path="/mobile" element={<Navigate to="/mobile/login" replace />} />
      </Routes>
    </MobileAuthProvider>
    </MobileThemeProvider>
  );
};

const AppRoutes = () => {
  const { user, loading, pendingTenantSelection } = useAuth();
  const location = useLocation();

  // Public routes that should render immediately without waiting for auth
  const publicPaths = ['/privacy', '/eula', '/quote-approval', '/docket-sign', '/ea/onboard'];
  const isPublicRoute = publicPaths.some(p => location.pathname.startsWith(p)) || location.pathname.startsWith('/legal/');

  if (loading && !isPublicRoute) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // User is considered "logged in" only if they have a user AND don't need to select a tenant
  const isFullyLoggedIn = user && !pendingTenantSelection;

  return (
    <Routes>
      <Route path="/login" element={isFullyLoggedIn ? <Navigate to="/dashboard" replace /> : <Login />} />
      <Route path="/free-trial" element={user ? <Navigate to="/dashboard" replace /> : <FreeTrial />} />
      <Route path="/features" element={<Features />} />
      <Route path="/support-plans" element={<SupportPlans />} />
      <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <Navigate to="/v2/forgot-password" replace />} />
      <Route path="/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <ResetPassword />} />
      {/* V2 (unified_users) password reset */}
      <Route path="/v2/forgot-password" element={user ? <Navigate to="/dashboard" replace /> : <V2ForgotPassword />} />
      <Route path="/v2/reset-password" element={user ? <Navigate to="/dashboard" replace /> : <V2ResetPassword />} />
      <Route path="/set-password" element={user ? <Navigate to="/dashboard" replace /> : <SetPassword />} />
      <Route path="/signup-success" element={<SignupSuccess />} />
      {/* Isolated theme comparison sandbox (does not affect live styling) */}
      <Route path="/theme-preview" element={<ThemePreviewIndex />} />
      <Route path="/theme-preview/1" element={<ThemePreview1 />} />
      <Route path="/theme-preview/2" element={<ThemePreview2 />} />
      <Route path="/quote-approval/:token" element={<QuoteApproval />} />
      <Route path="/docket-sign/:token" element={<DocketSign />} />
      <Route path="/compliance-pack/:token" element={<CompliancePackPublic />} />
      <Route path="/privacy" element={<PolicyPage />} />
      <Route path="/eula" element={<PolicyPage />} />
      <Route path="/legal/:docType" element={<PolicyPage />} />
      <Route path="/ea/onboard" element={<EarlyAdopterOnboarding />} />
      <Route path="/onboarding" element={
        <ProtectedRoute>
          <OnboardingWizard />
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      
      <Route element={<ProtectedRoute allowSuspended><Layout /></ProtectedRoute>}>
        <Route path="/getting-started" element={<GettingStarted />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/equipment" element={
          <ProtectedRoute permission="equipment.read">
            <Equipment />
          </ProtectedRoute>
        } />
        <Route path="/attachments" element={<Navigate to="/equipment" replace />} />
        <Route path="/operators" element={
          <ProtectedRoute permission="operators.read">
            <Operators />
          </ProtectedRoute>
        } />
        <Route path="/workers" element={
          <ProtectedRoute permission="operators.read">
            <Operators />
          </ProtectedRoute>
        } />
        <Route path="/customers" element={
          <ProtectedRoute permission="customers.read">
            <Customers />
          </ProtectedRoute>
        } />
        <Route path="/sites" element={<Navigate to="/customers?tab=sites" replace />} />
        <Route path="/suppliers" element={
          <ProtectedRoute permission="suppliers.read">
            <Suppliers />
          </ProtectedRoute>
        } />
        <Route path="/projects" element={
          <ProtectedRoute permission="projects.read">
            <Projects />
          </ProtectedRoute>
        } />
        <Route path="/tips-materials" element={
          <ProtectedRoute permission="tips_materials.read">
            <TipsMaterials />
          </ProtectedRoute>
        } />
        <Route path="/jobs" element={<Navigate to="/schedule" replace />} />
        <Route path="/schedule" element={
          <ProtectedRoute permission="schedule.read">
            <Schedule />
          </ProtectedRoute>
        } />
        <Route path="/schedule/tower-crane/new" element={
          <ProtectedRoute permission="jobs.write">
            <TowerCraneJobCreate />
          </ProtectedRoute>
        } />
        <Route path="/schedule/tower-crane/:jobId" element={
          <ProtectedRoute permission="jobs.write">
            <TowerCraneJobCreate />
          </ProtectedRoute>
        } />
        <Route path="/dry-hire-invoicing" element={
          <ProtectedRoute permission="accounting.read">
            <DryHireInvoicingDashboard />
          </ProtectedRoute>
        } />
        <Route path="/my-jobs" element={<MyJobs />} />
        <Route path="/reports" element={
          <ProtectedRoute permission="reports.read">
            <Reports />
          </ProtectedRoute>
        } />
        <Route path="/dockets" element={
          <ProtectedRoute permission="dockets.read">
            <Dockets />
          </ProtectedRoute>
        } />
        <Route path="/quotes" element={
          <ProtectedRoute permission="quotes.read">
            <Quotes />
          </ProtectedRoute>
        } />
        <Route path="/quotes/new" element={
          <ProtectedRoute permission="quotes.write">
            <QuoteEdit />
          </ProtectedRoute>
        } />
        <Route path="/quotes/:id" element={
          <ProtectedRoute permission="quotes.read">
            <QuoteView />
          </ProtectedRoute>
        } />
        <Route path="/quotes/:id/edit" element={
          <ProtectedRoute permission="quotes.write">
            <QuoteEdit />
          </ProtectedRoute>
        } />
        <Route path="/timesheets" element={
          <ProtectedRoute permission="timesheets.read">
            <Timesheets />
          </ProtectedRoute>
        } />
        <Route path="/planning-board" element={
          <ProtectedRoute permission="schedule.read">
            <PlanningBoard />
          </ProtectedRoute>
        } />
        <Route path="/utilisation" element={
          <ProtectedRoute permission="schedule.read">
            <Utilisation />
          </ProtectedRoute>
        } />
        <Route path="/compliance" element={
          <ProtectedRoute permission="compliance.read">
            <Compliance />
          </ProtectedRoute>
        } />
        <Route path="/forms" element={
          <ProtectedRoute permission="forms.read">
            <Forms />
          </ProtectedRoute>
        } />
        <Route path="/documents" element={
          <ProtectedRoute permission="documents.read">
            <Documents />
          </ProtectedRoute>
        } />
        <Route path="/chat" element={
          <ProtectedRoute permission="chat.read">
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="/chat/:roomId" element={
          <ProtectedRoute permission="chat.read">
            <ChatPage />
          </ProtectedRoute>
        } />
        <Route path="/data-management" element={
          <ProtectedRoute roles={["admin"]}>
            <DataManagement />
          </ProtectedRoute>
        } />
        <Route path="/data-import" element={
          <ProtectedRoute roles={["admin"]}>
            <DataImport />
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute roles={["admin"]} allowSuspended>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="/product-roadmap" element={
          <ProtectedRoute permission="dashboard.read">
            <ProductRoadmap />
          </ProtectedRoute>
        } />
        <Route path="/help" element={
          <ProtectedRoute>
            <HelpCenter />
          </ProtectedRoute>
        } />
        <Route path="/release-notes" element={
          <ProtectedRoute>
            <ReleaseNotes />
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute allowSuspended>
            <Support />
          </ProtectedRoute>
        } />
        <Route path="/support/tickets/:ticketId" element={
          <ProtectedRoute allowSuspended>
            <TenantTicketDetail />
          </ProtectedRoute>
        } />
        <Route path="/users" element={<Navigate to="/settings" replace />} />
      </Route>
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <EnvBanner />
        <AppRouter />
      </ThemeProvider>
    </BrowserRouter>
  );
}

// Router component that decides between mobile, super-admin, and desktop
const AppRouter = () => {
  const location = useLocation();
  const isMobileRoute = location.pathname.startsWith('/mobile');
  const isTenantMobileRoute = location.pathname.startsWith('/tenant-mobile');
  const isSuperAdminRoute = location.pathname.startsWith('/super-admin');

  // Check for new app versions and auto-refresh
  useVersionCheck();

  if (isSuperAdminRoute) {
    return (
      <SuperAdminProvider>
        <SuperAdminSupportNotificationsProvider>
          <SuperAdminRoutes />
          <Toaster position="top-right" richColors />
        </SuperAdminSupportNotificationsProvider>
      </SuperAdminProvider>
    );
  }

  if (isMobileRoute) {
    return (
      <MobileThemeProvider>
      <MobileAuthProvider>
        <MobileAppWithLanguage />
        <Toaster position="top-center" richColors className="mobile-toaster" />
      </MobileAuthProvider>
      </MobileThemeProvider>
    );
  }

  // Legacy tenant-mobile URLs redirect to unified mobile portal
  if (isTenantMobileRoute) {
    return <Navigate to="/mobile/dashboard" replace />;
  }

  return (
    <AuthProvider>
      <SupportNotificationsProvider>
        <DesktopAppWithLanguage />
        <Toaster position="top-right" richColors />
      </SupportNotificationsProvider>
    </AuthProvider>
  );
};

// Super Admin Routes
const SuperAdminRoutes = () => {
  const { admin, loading, mustChangePassword, tempToken, completePasswordChange } = useSuperAdmin();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd108]"></div>
      </div>
    );
  }

  // Intercept if password change is required
  if (admin && mustChangePassword) {
    return (
      <SuperAdminForcePasswordChange
        token={tempToken}
        admin={admin}
        onPasswordChanged={completePasswordChange}
      />
    );
  }

  // Hard-block admin-only SA pages for non-admin roles (support/manager),
  // even via direct URL, so impersonation reflects real restrictions.
  const requireAdmin = (element) =>
    admin?.role === 'admin' ? element : <Navigate to="/super-admin/dashboard" replace />;


  return (
    <Routes>
      <Route path="/super-admin/login" element={
        admin ? <Navigate to="/super-admin/dashboard" replace /> : <SuperAdminLogin />
      } />
      <Route path="/super-admin/forgot-password" element={
        admin ? <Navigate to="/super-admin/dashboard" replace /> : <SuperAdminForgotPassword />
      } />
      <Route path="/super-admin/reset-password" element={
        admin ? <Navigate to="/super-admin/dashboard" replace /> : <SuperAdminResetPassword />
      } />
      <Route path="/super-admin" element={
        admin ? <SuperAdminLayout /> : <Navigate to="/super-admin/login" replace />
      }>
        <Route index element={<Navigate to="/super-admin/dashboard" replace />} />
        <Route path="dashboard" element={<SuperAdminDashboard />} />
        <Route path="tenants" element={<TenantsList />} />
        <Route path="tenants/new" element={<TenantOnboarding />} />
        <Route path="tenants/:clientId" element={<TenantDetail />} />
        <Route path="demo-tenants" element={requireAdmin(<DemoTenantsManagement />)} />
        <Route path="feedback" element={requireAdmin(<FeedbackManagement />)} />
        <Route path="tickets" element={<TicketsList />} />
        <Route path="tickets/:ticketId" element={<TicketDetail />} />
        <Route path="sessions" element={<SessionsManagement />} />
        <Route path="roadmap" element={requireAdmin(<RoadmapManagement />)} />
        <Route path="help" element={<HelpManagement />} />
        <Route path="emails" element={requireAdmin(<SuperAdminEmails />)} />
        <Route path="agents" element={requireAdmin(<AdminsList />)} />
        <Route path="security" element={<TwoFactorSetup />} />
        <Route path="settings" element={requireAdmin(<SystemSettings />)} />
        <Route path="auth-migration" element={requireAdmin(<AuthMigration />)} />
        <Route path="feature-flags" element={requireAdmin(<FeatureFlags />)} />
        <Route path="migrations" element={requireAdmin(<Migrations />)} />
        <Route path="allocate-migration" element={requireAdmin(<AllocateMigration />)} />
        <Route path="announcements" element={requireAdmin(<Announcements />)} />
      </Route>
      <Route path="/super-admin/*" element={<Navigate to="/super-admin/login" replace />} />
    </Routes>
  );
};

// Desktop app with language provider
const DesktopAppWithLanguage = () => {
  const { user, token, mustChangePassword, clearMustChangePassword, api, logout, setToken } = useAuth();
  
  // Show force password change screen if user must change password
  if (user && mustChangePassword) {
    return (
      <ForcePasswordChange 
        token={token} 
        user={user} 
        onPasswordChanged={clearMustChangePassword} 
      />
    );
  }
  
  return (
    <LanguageProvider 
      userType="admin" 
      userId={user?.id} 
      token={token}
    >
      <TierProvider>
        <ChatProvider>
          <FeatureFlagsProvider>
            <TourProvider>
              <SubTourProvider>
                <AppRoutes />
                <ChatPanel />
                <TourOverlay />
                <SubTourOverlay />
                {/* Session timeout warning modal */}
                {user && token && (
                  <SessionTimeoutModal 
                    token={token}
                    api={api}
                    onLogout={logout}
                    onExtendSession={() => {}}
                    onTokenRefresh={(t) => { if (t) setToken(t); }}
                  />
                )}
              </SubTourProvider>
            </TourProvider>
          </FeatureFlagsProvider>
        </ChatProvider>
      </TierProvider>
    </LanguageProvider>
  );
};

// Mobile app with language provider
const MobileAppWithLanguage = () => {
  const { operator, token } = useMobileAuth();
  
  return (
    <LanguageProvider 
      userType="operator" 
      userId={operator?.id} 
      token={token}
    >
      <MobileChatProvider>
        <MobileAppRoutes />
      </MobileChatProvider>
    </LanguageProvider>
  );
};

// Mobile feature gate component
const MobileFeatureGate = ({ feature, children }) => {
  const { operator, hasFeature } = useMobileAuth();
  
  if (!operator) {
    return <Navigate to="/mobile/login" replace />;
  }
  
  if (!hasFeature(feature)) {
    return <Navigate to="/mobile/dashboard" replace />;
  }
  
  return children;
};

// Mobile admin flag gate — any office role + basic_mobile_admin flag
const MobileAdminFlagGate = ({ children }) => {
  const { operator, isTenantAdmin, isFlagEnabled, platformFlagsLoaded } = useMobileAuth();
  if (!operator) return <Navigate to="/mobile/login" replace />;
  // Non-office users never reach here; wait for flags before gating (avoids
  // a redirect race on hard-refresh / deep-link before flags resolve).
  if (!isTenantAdmin()) return <Navigate to="/mobile/dashboard" replace />;
  if (!platformFlagsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }
  if (!isFlagEnabled('basic_mobile_admin')) {
    return <Navigate to="/mobile/dashboard" replace />;
  }
  return children;
};

// Permission-aware mobile gate — checks permission, optional feature flag, optional adminOnly
const MobilePermissionGate = ({ permission, feature, adminOnly, children }) => {
  const { operator, can, isAdmin, hasFeature, isFlagEnabled, platformFlagsLoaded } = useMobileAuth();
  if (!operator) return <Navigate to="/mobile/login" replace />;

  // Wait for platform flags before deciding (avoids redirect race on deep-link)
  if (!platformFlagsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  // Keep the basic_mobile_admin flag check until Phase 6 removes it
  if (!isFlagEnabled('basic_mobile_admin')) {
    return <Navigate to="/mobile/dashboard" replace />;
  }

  // Admin-only screens (Data Management, Settings)
  if (adminOnly && !isAdmin()) return <Navigate to="/mobile/dashboard" replace />;

  // Permission check
  if (permission && !can(permission)) return <Navigate to="/mobile/dashboard" replace />;

  // Feature flag check (tenant plan features like timesheets, forms, etc.)
  if (feature && !hasFeature(feature)) return <Navigate to="/mobile/dashboard" replace />;

  return children;
};

// Separate component for mobile routes
const MobileAppRoutes = () => {
  const { operator, loading, hasFeature, hasMobileQuotesAccess, canAccessSchedule, subscriptionStatus } = useMobileAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1a1a]">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  // Suspended or cancelled tenants: show paywall (admin) or blocked message (worker)
  // Allow login/forgot/reset routes through so users can re-authenticate
  if (operator && (subscriptionStatus === 'suspended' || subscriptionStatus === 'cancelled')) {
    return (
      <>
        <Routes>
          <Route path="/mobile/login" element={<Navigate to="/mobile/dashboard" replace />} />
          <Route path="/mobile/*" element={<MobileSubscriptionRequired />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <MobileImpersonationBanner />
      <Routes>
      <Route path="/mobile/login" element={operator ? <Navigate to="/mobile/dashboard" replace /> : <MobileLogin />} />
      <Route path="/mobile/forgot-password" element={operator ? <Navigate to="/mobile/dashboard" replace /> : <MobileForgotPassword />} />
      <Route path="/mobile/reset-password" element={operator ? <Navigate to="/mobile/dashboard" replace /> : <MobileResetPassword />} />
      <Route path="/mobile/dashboard" element={operator ? <MobileDashboard /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/settings" element={operator ? <MobileSettings /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/jobs" element={operator ? <MobileJobs /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/jobs/:jobId" element={operator ? <MobileJobDetail /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/docket/create/:jobId" element={operator ? <MobileCreateDocket /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/timesheet" element={
        <MobileFeatureGate feature="timesheets">
          <MobileTimesheet />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/notifications" element={operator ? <MobileNotifications /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/chat" element={
        <MobileFeatureGate feature="messaging">
          <MobileChat />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/chat/:roomId" element={
        <MobileFeatureGate feature="messaging">
          <MobileChat />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/forms" element={
        <MobileFeatureGate feature="forms">
          <MobileForms />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/forms/submit" element={
        <MobileFeatureGate feature="forms">
          <MobileFormSubmission />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/forms/submission/:submissionId" element={
        <MobileFeatureGate feature="forms">
          <MobileSubmissionDetail />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/help" element={operator ? <MobileHelp /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/compliance" element={
        <MobileFeatureGate feature="compliance">
          <MobileCompliance />
        </MobileFeatureGate>
      } />
      <Route path="/mobile/equipment" element={<MobileEquipment />} />
      <Route path="/mobile/equipment/:equipmentId" element={<MobileEquipmentDetail />} />
      <Route path="/mobile/my-dockets" element={<MobileMyDockets />} />
      <Route path="/mobile/my-dockets/:docketId" element={<MobileMyDocketDetail />} />
      <Route path="/mobile/quotes" element={
        operator && hasMobileQuotesAccess() ? <MobileQuotes /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/quotes/new" element={
        operator && hasMobileQuotesAccess() ? <MobileQuoteEdit /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/quotes/:id" element={
        operator && hasMobileQuotesAccess() ? <MobileQuoteDetail /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/quotes/:id/edit" element={
        operator && hasMobileQuotesAccess() ? <MobileQuoteEdit /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/inspections" element={
        operator && operator.is_yard_dispatcher ? <MobileInspections /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/inspections/:inspectionId" element={
        operator && operator.is_yard_dispatcher ? <MobileInspectionDetail /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/more" element={operator ? <MobileMore /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/documents" element={operator ? <MobileDocuments /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/manage" element={<MobilePermissionGate permission="dashboard.read"><MobileManage /></MobilePermissionGate>} />
      <Route path="/mobile/manage/dockets" element={<MobilePermissionGate permission="dockets.read"><MobileManageDockets /></MobilePermissionGate>} />
      <Route path="/mobile/manage/customers" element={<MobilePermissionGate permission="customers.read"><MobileManageCustomers /></MobilePermissionGate>} />
      <Route path="/mobile/manage/customers/:customerId/sites" element={<MobilePermissionGate permission="customers.read"><MobileManageSites /></MobilePermissionGate>} />
      <Route path="/mobile/manage/equipment" element={<MobilePermissionGate permission="equipment.read"><MobileManageEquipment /></MobilePermissionGate>} />
      <Route path="/mobile/manage/suppliers" element={<MobilePermissionGate permission="suppliers.read"><MobileManageSuppliers /></MobilePermissionGate>} />
      <Route path="/mobile/manage/worker-types" element={<MobilePermissionGate adminOnly><MobileManageWorkerTypes /></MobilePermissionGate>} />
      <Route path="/mobile/manage/operators" element={<MobilePermissionGate permission="operators.read"><MobileManageOperators /></MobilePermissionGate>} />
      <Route path="/mobile/manage/data" element={<MobilePermissionGate adminOnly><MobileDataManagement /></MobilePermissionGate>} />
      <Route path="/mobile/manage/settings" element={<MobilePermissionGate adminOnly><MobileBusinessSettings /></MobilePermissionGate>} />
      <Route path="/mobile/leave" element={operator ? <MobileLeave /> : <Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/schedule" element={
        operator && canAccessSchedule() ? <MobileSchedule /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/schedule/new" element={
        operator && canAccessSchedule() ? <MobileScheduleCreateJob /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile/schedule/:jobId" element={
        operator && canAccessSchedule() ? <MobileScheduleJobDetail /> : <Navigate to="/mobile/dashboard" replace />
      } />
      <Route path="/mobile" element={<Navigate to="/mobile/login" replace />} />
      <Route path="/mobile/*" element={<Navigate to="/mobile/login" replace />} />
    </Routes>
    </>
  );
};

export default App;
